import { BottomDrawer } from '@components/bottomDrawer';
import { Button } from '@components/button';
import { TTableBase } from '@components/table/types/TableBase.type';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { Box, Grid, Typography } from '@mui/material';
import { useMemo } from 'react';

export type MobileRowSelectedViewProps<T extends TTableBase> = {
  row?: T['items'][0] | null;
  columns: TableGridBaseColDef<T['items'][0]>[];
  onClose: () => void;
  columnVisibilityModel: { [key: string]: boolean };
};

export function MobileRowSelectedView<T extends TTableBase>({
  columnVisibilityModel,
  columns,
  row,
  onClose,
}: MobileRowSelectedViewProps<T>) {
  const columnsToDisplay = useMemo(() => {
    return columns?.filter((col) => col?.mobileConfig?.hidden?.xs !== true);
  }, [columns]);

  const actionMenuItems = useMemo(() => {
    const actionMenuCol: any = columns?.find((col) => col?.mobileConfig?.actionMenu);
    if (!actionMenuCol) {
      return null;
    }

    return actionMenuCol.valueGetter ? actionMenuCol.valueGetter(row, [actionMenuCol.field], row) : null;
  }, [columns, row]);

  if (!row) {
    return null;
  }

  // filter out columns that are not visible, then map the columns to be rendered
  const itemsToRender = columnsToDisplay
    ?.filter((t) => !columnVisibilityModel || (columnVisibilityModel[t.field] && t.mobileConfig?.hidden?.xs !== true))
    ?.map((col) => {
      const value = col.valueGetter ? col.valueGetter(row[col.field], row) : row[col.field];

      return {
        field: col.field,
        headerName: col.headerName,
        value,
        renderValue: col.renderCell ? (
          col.renderCell({ row, value })
        ) : (
          <Typography variant='bodySmall' fontWeight='bold' css>
            {value ? value?.toString() : '-'}
          </Typography>
        ),
        isHeaderColumn: col?.mobileConfig?.mobileLockedColumn,
      };
    });

  // if no mobile header set attempt to use first
  if (!itemsToRender.find((i) => i.isHeaderColumn)) {
    itemsToRender[0].isHeaderColumn = true;
  }

  return (
    <BottomDrawer
      isOpen={true}
      toggleDrawer={onClose}
      header={itemsToRender
        ?.filter((i) => i.isHeaderColumn)
        ?.map((item: any) => {
          return <Box key={item.field}>{item.renderValue}</Box>;
        })}
      footer={
        Array.isArray(actionMenuItems) && actionMenuItems?.length > 0 ? (
          <Grid container justifyContent='space-evenly'>
            {actionMenuItems.map((item: any, index: number) => {
              return (
                <Button variant='contained' key={index} onClick={item?.onClick}>
                  {item.text}
                </Button>
              );
            })}
          </Grid>
        ) : (
          (actionMenuItems ?? undefined)
        )
      }
    >
      <Grid container size={{ xs: 12 }} sx={{ padding: '20px' }} spacing={2}>
        {itemsToRender
          ?.filter((i) => !i.isHeaderColumn)
          ?.map((item: any) => {
            return (
              <Grid
                size={{ xs: 12 }}
                sx={{
                  justifyContent: 'space-between',
                  display: 'flex',
                  textAlign: 'right',
                }}
                key={item.field}
              >
                <Typography variant='bodySmall' css sx={{ textWrap: 'nowrap', paddingRight: 5, textAlign: 'left' }}>
                  {item.headerName}
                </Typography>

                {item.renderValue}
              </Grid>
            );
          })}
      </Grid>
    </BottomDrawer>
  );
}
